import { Autocomplete, Box, FormControl, FormHelperText, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { ButtonComponent, FunctionsEnum, isNumber, TextFieldComponent } from '@watter/shared-components';
import { ErrorMessage, Form, Formik } from 'formik';
import React, { useState } from 'react';
import { GetCountries } from 'react-country-state-city';
import { useDispatch } from 'react-redux';
import { CREATE_ADMIN, UPDATE_ADMIN } from '../../graphql/mutations';
import { GET_MANAGE_ADMIN_ROLES_LIST } from '../../graphql/queries';
import { usePermissions } from '../../custom_hooks/permissions';
import { client } from '../../service/apolloClient';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { MainWrapper } from '../common-styles';
import { ManageAdminValidationSchema } from './manage-admin-validation-schema';
import { IFormObj } from './type';

interface CreateAdminProps {
    onClose: () => void;
    adminInfo: any;
    fetchMangeAdminListData: () => void;
    isEdit: boolean;
}

export const CreateAdmin: React.FC<CreateAdminProps> = ({ onClose, adminInfo, fetchMangeAdminListData, isEdit }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [initialForm, setInitialForm] = useState<IFormObj>({
        firstName: '',
        lastName: '',
        country: '',
        mobileNo: '',
        email: '',
        role: null,
    });
    const [selectedCountryPhoneCode, setCountryPhoneCode] = useState('');
    const [countriesList, setCountriesList] = useState([]);
    const [selectedRole, setSelectedRole] = useState<number | null>(null);
    const [roles, setAdminRolesList] = useState<any[]>([]);
    const { canCreate, canEdit } = usePermissions();
    React.useEffect(() => {
        if (!isEdit) return;

        const getCountryIso2 = () => {
            if (adminInfo?.user_addresses && adminInfo?.user_addresses.length) {
                return adminInfo.user_addresses[0]?.country;
            }
            return 'IN';
        };

        const getPhoneNumber = () => {
            if (
                adminInfo.phone_number &&
                selectedCountryPhoneCode &&
                adminInfo.phone_number.startsWith(selectedCountryPhoneCode)
            ) {
                return adminInfo.phone_number.slice(selectedCountryPhoneCode.length);
            }
            return adminInfo.phone_number || '';
        };

        const countryIso2 = getCountryIso2();

        const data: any = countriesList.filter((i: any) => i.iso2 === countryIso2);
        const phoneCode = data && data.length && data[0]?.phone_code ? data[0]?.phone_code : '+91';
        setCountryPhoneCode(phoneCode);

        setInitialForm({
            firstName: adminInfo.first_name || '',
            lastName: adminInfo.last_name || '',
            email: adminInfo.email || '',
            mobileNo: getPhoneNumber(),
            role: adminInfo.role_id || '',
            country: countryIso2,
        });

        setSelectedRole(adminInfo.role_id);
    }, [
        isEdit,
        countriesList,
        adminInfo?.email,
        adminInfo?.first_name,
        adminInfo?.last_name,
        adminInfo?.phone_number,
        adminInfo?.role_id,
        adminInfo?.user_addresses,
        selectedCountryPhoneCode,
    ]);
    const fetchAdminRolesList = async () => {
        const { data } = await client.query({ query: GET_MANAGE_ADMIN_ROLES_LIST });
        const adminRolesList = data?.getAdminRolesList;
        const newAdminRolesList: any[] = [];
        adminRolesList.forEach((role: any) => {
            const newRole = { ...role };
            newRole.name = newRole.name.charAt(0).toUpperCase() + newRole.name.slice(1);
            newAdminRolesList.push(newRole);
        });
        setAdminRolesList(newAdminRolesList);
    };
    React.useEffect(() => {
        GetCountries().then((result: any) => {
            const filteredCountries: any = [];
            result.forEach((country: any) => {
                if (country.name === 'India' || country.name === 'United States') {
                    const countryWithPhoneCode = { ...country, phone_code: `+${country.phone_code}` };
                    filteredCountries.push(countryWithPhoneCode);
                }
                setCountriesList(filteredCountries);
            });
        });
        fetchAdminRolesList();
    }, []);
    const handleCreateAdminAPI = async (formData: IFormObj) => {
        try {
            dispatch(showLoader());
            const input: any = {
                first_name: formData.firstName,
                last_name: formData.lastName,
                role_id: formData.role,
            };

            if (isEdit) {
                input.cognito_id = adminInfo.cognito_id;
            }
            if (!isEdit) {
                input.email = formData.email;
                input.phone_number = selectedCountryPhoneCode + formData.mobileNo;
                input.country = formData.country;
            }
            const { data } = await client.mutate({
                mutation: isEdit ? UPDATE_ADMIN : CREATE_ADMIN,
                variables: { input },
            });

            dispatch(
                openToast({
                    show: true,
                    message: isEdit
                        ? data?.updateAdmin?.message || t('updated_admin_successfully_message')
                        : data?.addAdmin?.message || t('created_admin_successfully_message'),
                    severity: 'success',
                    isAlertOpen: true,
                }),
            );
            onClose();
            fetchMangeAdminListData();
        } catch (error: any) {
            console.error('Create admin failed', error);
        } finally {
            dispatch(hideLoader());
        }
    };
    const handleCountryChange = (e: any, setFieldValue: any) => {
        const data: any = countriesList.filter((i: any) => i.id === e?.id);
        setFieldValue('country', data && data[0]?.iso2);
        setCountryPhoneCode(data && data[0]?.phone_code);
    };
    const handleRoleSelection = (e: any, setFieldValue: any) => {
        setSelectedRole(e?.id);
        setFieldValue('role', e?.id);
    };
    return (
        <Box>
            <Typography px={4} pt={4} variant="h4">
                {isEdit ? t('edit_admin') : t('create_admin')}
            </Typography>
            <MainWrapper className="detailsForm cstUpdateInstaller">
                <Box width="100%">
                    <Formik
                        initialValues={initialForm}
                        onSubmit={async values => {
                            await handleCreateAdminAPI(values);
                        }}
                        enableReinitialize
                        validationSchema={ManageAdminValidationSchema(t)}
                    >
                        {({ values, setFieldValue, submitForm, resetForm, errors }) => {
                            return (
                                <Form>
                                    <Box height="calc(100dvh - 136px)" py={1.5} overflow="auto" px={4}>
                                        <Grid container flexGrow={1} className="details profileDetails" spacing={2}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Stack>
                                                    <Typography variant="subtitle1" mb={1} color="info.100">
                                                        {t('first_name')}
                                                    </Typography>
                                                    <TextFieldComponent
                                                        name="firstName"
                                                        placeholder={t('enter_first_name')}
                                                        onChange={(e: { target: { value: any } }) => {
                                                            setFieldValue('firstName', e.target.value);
                                                        }}
                                                        value={values.firstName}
                                                    />

                                                    {errors?.firstName && (
                                                        <Typography variant="subtitle1" className="errorText">
                                                            {errors?.firstName}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Stack>
                                                    <Typography variant="subtitle1" mb={1} color="info.100">
                                                        {t('last_name')}
                                                    </Typography>
                                                    <TextFieldComponent
                                                        name="lastName"
                                                        placeholder={t('enter_last_name')}
                                                        onChange={(e: { target: { value: any } }) =>
                                                            setFieldValue('lastName', e.target.value)
                                                        }
                                                        value={values.lastName}
                                                    />

                                                    {errors?.lastName && (
                                                        <Typography variant="subtitle1" className="errorText">
                                                            {errors?.lastName}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Grid>
                                            <Grid size={12}>
                                                <Stack>
                                                    <Typography variant="subtitle1" mb={1} color="info.100">
                                                        {t('email')}
                                                    </Typography>
                                                    <TextFieldComponent
                                                        name="email"
                                                        placeholder={t('enter_email')}
                                                        onChange={(e: { target: { value: any } }) =>
                                                            setFieldValue('email', e.target.value)
                                                        }
                                                        value={values.email}
                                                        disabled={isEdit}
                                                    />
                                                    <FormHelperText error>
                                                        <ErrorMessage name="email" />
                                                    </FormHelperText>
                                                </Stack>
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <FormControl className="cstSelectProfile cstState" fullWidth>
                                                    <Typography variant="subtitle1" color="info.100">
                                                        {t('country')}
                                                    </Typography>
                                                    <Autocomplete
                                                        options={countriesList}
                                                        getOptionLabel={(option: any) => option.name || ''}
                                                        value={
                                                            selectedCountryPhoneCode
                                                                ? countriesList.find(
                                                                      (country: any) =>
                                                                          country.phone_code ===
                                                                          selectedCountryPhoneCode,
                                                                  ) || null
                                                                : null
                                                        }
                                                        disabled={isEdit}
                                                        onChange={(event, newValue) =>
                                                            handleCountryChange(newValue, setFieldValue)
                                                        }
                                                        renderInput={params => (
                                                            <TextField
                                                                {...params}
                                                                name="country"
                                                                placeholder={t('select_country')}
                                                                InputLabelProps={{ sx: { display: 'none' } }}
                                                            />
                                                        )}
                                                    />
                                                    <FormHelperText error>
                                                        <ErrorMessage name="country" />
                                                    </FormHelperText>
                                                </FormControl>
                                            </Grid>
                                            <Grid size={12}>
                                                <FormControl fullWidth>
                                                    <Typography variant="subtitle1" mb={1} color="info.100">
                                                        {t('mobile_number')}
                                                    </Typography>
                                                    <TextFieldComponent
                                                        variant="outlined"
                                                        id=""
                                                        error={false}
                                                        size="small"
                                                        name="mobileNo"
                                                        type="string"
                                                        placeholder={`${t('enter_mobile_number')}`}
                                                        onChange={(e: { target: { value: any } }) => {
                                                            if (isNumber(e.target.value))
                                                                setFieldValue('mobileNo', e.target.value);
                                                        }}
                                                        maxLength={10}
                                                        value={values.mobileNo}
                                                        disabled={isEdit}
                                                    />
                                                </FormControl>
                                                <FormHelperText error>
                                                    <ErrorMessage name="mobileNo" />
                                                </FormHelperText>
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <FormControl className="cstSelectProfile cstState" fullWidth>
                                                    <Typography variant="subtitle1" color="info.100">
                                                        {t('role')}
                                                    </Typography>
                                                    <Autocomplete
                                                        options={roles}
                                                        getOptionLabel={(option: any) => option.name || ''}
                                                        value={
                                                            selectedRole
                                                                ? roles.find((role: any) => role.id === selectedRole) ||
                                                                  null
                                                                : null
                                                        }
                                                        disabled={selectedRole === 3}
                                                        onChange={(event, newValue) =>
                                                            handleRoleSelection(newValue, setFieldValue)
                                                        }
                                                        renderInput={params => (
                                                            <TextField
                                                                {...params}
                                                                name="role"
                                                                placeholder={t('select_role')}
                                                                InputLabelProps={{ sx: { display: 'none' } }}
                                                            />
                                                        )}
                                                    />
                                                    <FormHelperText error>
                                                        <ErrorMessage name="role" />
                                                    </FormHelperText>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        alignItems="center"
                                        justifyContent="space-between"
                                        className="actionFooter"
                                        spacing={2}
                                        pt={2}
                                    >
                                        <ButtonComponent
                                            size="medium"
                                            variant="contained"
                                            fullWidth
                                            buttonName={t('cancel')}
                                            className="cstBtn"
                                            type="button"
                                            onClick={() => {
                                                resetForm();
                                                onClose();
                                            }}
                                        />
                                        <ButtonComponent
                                            size="medium"
                                            variant="contained"
                                            fullWidth
                                            type="button"
                                            buttonName={t('save')}
                                            className="cstBtn"
                                            disabled={
                                                !(
                                                    canCreate(FunctionsEnum.MANAGE_ADMINS) ||
                                                    canEdit(FunctionsEnum.MANAGE_ADMINS)
                                                )
                                            }
                                            onClick={submitForm}
                                        />
                                    </Stack>
                                </Form>
                            );
                        }}
                    </Formik>
                </Box>
            </MainWrapper>
        </Box>
    );
};
